import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useApp } from '../contexts/AppContext';
import { Button } from './ui/button';
import { ShoppingCart, Moon, Sun, Globe } from 'lucide-react';
import { translations } from '../data/menuData';

const BUTTON_HOVER = { scale: 1.1 };
const BUTTON_TAP = { scale: 0.9 };
const LOGO_HOVER = { scale: 1.02 };
const LOGO_TRANSITION = { type: 'spring', stiffness: 400 };
const HEADER_TRANSITION = { duration: 0.5, type: 'spring', stiffness: 100 };
const BADGE_TRANSITION = { type: 'spring', stiffness: 500, damping: 15 };

const IconButton = ({ onClick, testId, children, srText }) => (
  <motion.div whileHover={BUTTON_HOVER} whileTap={BUTTON_TAP}>
    <Button variant="ghost" size="icon" onClick={onClick} className="rounded-full" data-testid={testId}>
      {children}
      <span className="sr-only">{srText}</span>
    </Button>
  </motion.div>
);

const CartBadge = ({ count }) => {
  if (count === 0) return null;
  return (
    <motion.span
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      exit={{ scale: 0 }}
      transition={BADGE_TRANSITION}
      className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold"
      data-testid="cart-count"
    >
      {count}
    </motion.span>
  );
};

const LogoSection = ({ restaurantName, tagline }) => (
  <motion.div className="flex items-center gap-3" whileHover={LOGO_HOVER} transition={LOGO_TRANSITION}>
    <div className="h-16 w-16 md:h-20 md:w-20 rounded-full overflow-hidden flex items-center justify-center">
      <img src="/mirnas-logo.png" alt="Mirna's Pizza Logo" className="h-full w-full object-contain" />
    </div>
    <div>
      <h1 className="text-lg md:text-xl font-black tracking-tight">{restaurantName}</h1>
      <p className="text-xs text-muted-foreground hidden sm:block">{tagline}</p>
    </div>
  </motion.div>
);

export const MenuHeader = () => {
  const { cart, setIsCartOpen, theme, toggleTheme, language, toggleLanguage } = useApp();
  const t = translations[language];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={HEADER_TRANSITION}
      className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border"
    >
      <div className="mx-auto max-w-7xl px-4 py-4 md:px-8">
        <div className="flex items-center justify-between gap-4">
          <LogoSection restaurantName={t.restaurantName} tagline={t.tagline} />
          <div className="flex items-center gap-2">
            <IconButton onClick={toggleLanguage} testId="language-toggle-btn" srText="Toggle language">
              <Globe className="h-5 w-5" />
            </IconButton>
            <IconButton onClick={toggleTheme} testId="theme-toggle-btn" srText="Toggle theme">
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </IconButton>
            <motion.div whileHover={BUTTON_HOVER} whileTap={BUTTON_TAP}>
              <Button variant="ghost" size="icon" onClick={() => setIsCartOpen(true)} className="relative rounded-full" data-testid="cart-button">
                <ShoppingCart className="h-5 w-5" />
                <AnimatePresence><CartBadge count={cart.length} /></AnimatePresence>
                <span className="sr-only">Cart</span>
              </Button>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.header>
  );
};
